import type { GameResponse, LeaderboardResponse } from "../shared/competition.ts";

export class CompetitionError extends Error {
  constructor(message: string, readonly status = 0) {
    super(message);
  }
}

async function request<ResponseBody>(path: string, data?: Record<string, unknown>): Promise<ResponseBody> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: data ? "POST" : "GET",
      credentials: "same-origin",
      cache: "no-store",
      signal: AbortSignal.timeout(10_000),
      ...(data ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) } : {}),
    });
  } catch {
    throw new CompetitionError("Cannot reach the competition service. Please try again.");
  }
  if (!response.headers.get("Content-Type")?.includes("application/json")) {
    throw new CompetitionError("The competition service is not available on this host.", response.status);
  }
  const result = await response.json() as ResponseBody & { error?: string };
  if (!response.ok) throw new CompetitionError(result.error ?? "The request could not be completed.", response.status);
  return result;
}

export const competitionApi = {
  game: () => request<GameResponse>("/api/game"),
  begin: (date: string) => request<GameResponse>("/api/game/start", { date }),
  guess: (date: string, guess: string) => request<GameResponse>("/api/game/guess", { date, guess }),
  publish: (date: string, name: string) => request<GameResponse>("/api/leaderboard", { date, name }),
  remove: (date: string) => request<GameResponse>("/api/leaderboard/remove", { date }),
  board: (date: string) => request<LeaderboardResponse>(`/api/leaderboard?date=${encodeURIComponent(date)}`),
  transferClaim: (code: string) => request<{ ok: boolean }>("/api/transfer/claim", { code }),
};